/* eslint-disable no-restricted-syntax -- amber token for failed badge, theme has no warning colour */
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useIsPublishingEnabled } from '@utils/api/connections/use-is-publishing-enabled.tsx';
import { useGetDocumentPublishHistory } from '@utils/api/publishing/use-get-document-publish-history.tsx';
import PlatformIcon from '@components/profile/connections/platform-icon';
import { getPlatformSpec } from '@utils/types/connections/platforms.ts';
import { formatDateYearMonthDay } from '@utils/date.ts';
import type { PublishHistoryEntry } from '@utils/types/publishing/publish.ts';

const WARNING_ICON = '#B45309';

interface Props {
  sessionId: string;
  documentId: string;
}

/**
 * Lists prior publish attempts for the current document inside the About
 * panel. Hidden when the organization does not have the publishing
 * entitlement or when the document has no history to show.
 */
export default function PublishHistoryPanel({ sessionId, documentId }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { isEnabled: isPublishingEnabled } = useIsPublishingEnabled();

  const { data, isLoading } = useGetDocumentPublishHistory(
    sessionId,
    documentId,
    isPublishingEnabled
  );

  if (!isPublishingEnabled) return null;
  if (!isLoading && (data?.length ?? 0) === 0) return null;

  return (
    <section
      aria-labelledby="publish-history-heading"
      className="flex flex-col gap-2"
    >
      <h3
        id="publish-history-heading"
        className="text-sm font-semibold"
        style={{ color: getColour('normalText') }}
      >
        {t('publish-history-title')}
      </h3>
      <ul className="flex flex-col gap-2" aria-busy={isLoading}>
        {(data ?? []).map(entry => (
          <PublishHistoryRow key={entry.id} entry={entry} />
        ))}
      </ul>
    </section>
  );
}

function PublishHistoryRow({ entry }: { entry: PublishHistoryEntry }) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const spec = getPlatformSpec(entry.platform);

  const date = entry.published_at
    ? formatDateYearMonthDay(entry.published_at)
    : null;

  return (
    <li
      className="flex items-start gap-3 rounded-lg border p-2"
      style={{
        borderColor: getColour('hairline'),
        background: getColour('secondLayer')
      }}
    >
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
        style={{ background: getColour('thirdLayer') }}
      >
        <PlatformIcon platform={entry.platform} className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold">
            {spec?.displayName ?? entry.platform}
          </span>
          <StatusGlyph status={entry.status} />
          {date && (
            <span
              className="text-xs"
              style={{ color: getColour('inactiveText') }}
            >
              {date}
            </span>
          )}
        </div>
        {entry.target_ref?.title && (
          <span
            className="text-xs truncate"
            style={{ color: getColour('normalText') }}
            title={entry.target_ref.title}
          >
            {entry.target_ref.title}
          </span>
        )}
        {entry.published_by?.name && (
          <span
            className="text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            {t('publish-history-by', { name: entry.published_by.name })}
          </span>
        )}
        {entry.status === 'failed' && entry.failure?.message && (
          <span className="text-xs" style={{ color: WARNING_ICON }}>
            {entry.failure.message}
          </span>
        )}
        {entry.result_url && (
          <a
            href={entry.result_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium underline"
            style={{ color: getColour('activeText') }}
          >
            {t('publish-history-open')}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
      </div>
    </li>
  );
}

function StatusGlyph({ status }: { status: PublishHistoryEntry['status'] }) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  if (status === 'succeeded') {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs"
        style={{ color: getColour('success') }}
      >
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        {t('publish-status-succeeded')}
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs"
        style={{ color: WARNING_ICON }}
      >
        <AlertCircle className="h-3 w-3" aria-hidden="true" />
        {t('publish-status-failed')}
      </span>
    );
  }
  if (status === 'cancelled') {
    return (
      <span className="text-xs" style={{ color: getColour('inactiveText') }}>
        {t('publish-status-cancelled')}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs"
      style={{ color: getColour('inactiveText') }}
    >
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
      {t('publish-status-running')}
    </span>
  );
}
